import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const PortfolioContext = createContext(null);
const STORAGE_KEY = "equitybox.active_portfolio_id";

export function PortfolioProvider({ children }) {
  const { user } = useAuth();
  const [portfolios, setPortfolios] = useState([]);
  const [activeId, setActiveId] = useState(() => localStorage.getItem(STORAGE_KEY) || null);
  const [loading, setLoading] = useState(false);
  const [corpActions, setCorpActions] = useState({ actions: [], counts: { pending: 0 } });

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get("/portfolios");
      const list = r.data || [];
      setPortfolios(list);
      // If active not valid for this user, pick the default (or first)
      const ids = new Set(list.map((p) => p.id));
      const stored = localStorage.getItem(STORAGE_KEY);
      let next = stored && ids.has(stored) ? stored : null;
      if (!next) {
        const def = list.find((p) => p.is_default) || list[0];
        next = def?.id || null;
      }
      if (next !== activeId) setActiveId(next);
      if (next) localStorage.setItem(STORAGE_KEY, next);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => { if (user) load(); else { setPortfolios([]); setActiveId(null); } }, [user, load]);

  const select = useCallback((pid) => {
    setActiveId(pid);
    if (pid) localStorage.setItem(STORAGE_KEY, pid);
  }, []);

  const create = useCallback(async (name) => {
    const r = await api.post("/portfolios", { name });
    await load();
    select(r.data.id);
    return r.data;
  }, [load, select]);

  const rename = useCallback(async (pid, name) => {
    await api.put(`/portfolios/${pid}`, { name });
    await load();
  }, [load]);

  const remove = useCallback(async (pid) => {
    await api.delete(`/portfolios/${pid}`);
    if (pid === activeId) {
      localStorage.removeItem(STORAGE_KEY);
      setActiveId(null);
    }
    await load();
  }, [load, activeId]);

  const activePortfolio = useMemo(() => portfolios.find((p) => p.id === activeId) || null, [portfolios, activeId]);

  // ---- Automated corporate-actions (shared by the header badge + dashboard) ----
  const refreshCorpActions = useCallback(async () => {
    if (!activeId) { setCorpActions({ actions: [], counts: { pending: 0 } }); return; }
    try {
      const r = await api.get("/portfolio/corporate-actions");
      setCorpActions(r.data || { actions: [], counts: { pending: 0 } });
    } catch (e) { /* corporate actions optional; ignore */ }
  }, [activeId]);

  const applyCorpActions = useCallback(async (items) => {
    const payload = { actions: (items || []).map((a) => ({
      key: a.key, symbol: a.symbol, exchange: a.exchange, company_name: a.company_name,
      txn_type: a.txn_type, date: a.date, per_share: a.per_share || 0, ratio: a.ratio || 1,
      amount: a.amount || 0, shares_on_date: a.shares_on_date || 0,
    })) };
    const r = await api.post("/portfolio/corporate-actions/apply", payload);
    await refreshCorpActions();
    return r.data;
  }, [refreshCorpActions]);

  useEffect(() => { if (activeId) refreshCorpActions(); }, [activeId, refreshCorpActions]);

  const value = useMemo(
    () => ({ portfolios, activeId, activePortfolio, loading, select, create, rename, remove, reload: load,
             corpActions, refreshCorpActions, applyCorpActions }),
    [portfolios, activeId, activePortfolio, loading, select, create, rename, remove, load,
     corpActions, refreshCorpActions, applyCorpActions],
  );

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
}

export const usePortfolio = () => useContext(PortfolioContext);
